import React, { useEffect, useState, useRef } from 'react';
import {
  string,
  number,
  oneOf,
  func,
  arrayOf,
  shape,
  bool
} from 'prop-types';

import { MOBILE } from '@thd-olt-functional/utils';
import { ProductAddOnsPod } from '../../common/product-add-ons-pod';
import { useTippyToggle } from '../../../hooks/useTippyToggle';
import { AttachLaborTooltip } from './AttachLaborTooltip';
import { useAttachLabor } from '../../../hooks/useAttachLabor';

import {
  FULFILLMENT_METHOD_STH,
  FULFILLMENT_METHOD_BOPIS,
  ICONS,
  attachLaborType,
  cartBinding,
} from '../../../utils/product-add-on-constants';

const AttachLabor = ({
  channel,
  price,
  size,
  type,
  itemId,
  fulfillment,
  onChange,
  installProcess,
  priceIncludes,
  availableFulfillments,
  isInstallationSku,
  inStoreAssemblyAvailable,
}) => {
  const {
    heading,
    subHeading,
    userOptions,
    toolTipContentHeading,
    descriptionList,
    contentList,
    displayDisabledText,
    checked,
    setChecked
  } = useAttachLabor({
    fulfillment,
    availableFulfillments,
    inStoreAssemblyAvailable,
    isInstallationSku,
    type,
    priceIncludes,
    price,
    installProcess,
    onChange,
    itemId
  });

  const isTooltipExplicitlyOpened = useRef(false);
  const tooltipButtonRef = useTippyToggle(checked);

  useEffect(() => {
    if (
      channel !== MOBILE
      && !(isTooltipExplicitlyOpened.current)
      && checked
      && tooltipButtonRef?.current
      && !tooltipButtonRef?.current?._tippy?.state?.isVisible
    ) {
      tooltipButtonRef.current.click();
      isTooltipExplicitlyOpened.current = true;
    }
  });

  const handleChange = (event) => {
    if (event.target.value === cartBinding.ATTACH_LABOR_STORE_ASSEMBLY) {
      LIFE_CYCLE_EVENT_BUS.trigger(
        'attach-labor.change',
        {
          isOptedIn: event.target.checked,
          currentFulfillment: fulfillment,
          nextFulfillment: FULFILLMENT_METHOD_BOPIS
        }
      );
    } else {
      LIFE_CYCLE_EVENT_BUS.trigger(
        'attach-labor.change',
        {
          isOptedIn: event.target.checked,
          itemId,
          currentFulfillment: fulfillment,
          nextFulfillment: FULFILLMENT_METHOD_STH,
        }
      );
    }
    setChecked(event.target.checked);
    if (!event.target.checked) {
      isTooltipExplicitlyOpened.current = false;
    }
  };

  const tooltip = (
    <AttachLaborTooltip
      contentHeading={toolTipContentHeading}
      contentDescriptionList={(descriptionList || []).map((install) => (install.longText))}
      contentSubHeading="Price Includes"
      contentList={(contentList || []).map((priceInclude) => (priceInclude.shortText))}
      channel={channel}
      size={size}
      ref={tooltipButtonRef}
    />
  );

  return (
    <ProductAddOnsPod
      heading={heading}
      subHeading={subHeading}
      tooltip={tooltip}
      userOptions={userOptions}
      icon={ICONS.tool}
      iconHeight={36}
      iconWidth={36}
      onChange={handleChange}
      channel={channel}
      size={size}
      displayDisabledText={displayDisabledText}
    />
  );
};

AttachLabor.displayName = 'ProductAddOns-AttachLabor';

AttachLabor.propTypes = {
  /** What view to render ['desktop' || 'mobile']  */
  channel: oneOf(['desktop', 'mobile']),
  /** Render large or small version */
  size: oneOf(['large', 'small']),
  /** Starting Price */
  price: number,
  /** Type of labor ['install' || 'assembly'] */
  type: oneOf([attachLaborType.INSTALL, attachLaborType.ASSEMBLY]),
  /** For fulfillment type fulfillment */
  fulfillment: string.isRequired,
  /** Populates the tooltip */
  installProcess: arrayOf(shape({
    longText: string.isRequired
  })),
  /** Populates the tooltip */
  priceIncludes: arrayOf(shape({
    shortText: string.isRequired
  })),
  /** onChange handler passed in from parent */
  onChange: func.isRequired,
  /** installSku */
  itemId: string.isRequired,
  /** Available fulFillment methods */
  availableFulfillments: arrayOf(string).isRequired,
  /** Is Installation Sku (in-home install or assembly) */
  isInstallationSku: bool,
  /** Store Assembly option is present or not  */
  inStoreAssemblyAvailable: bool,
};

AttachLabor.defaultProps = {
  channel: 'desktop',
  size: 'large',
  inStoreAssemblyAvailable: false,
  isInstallationSku: false,
  price: 0,
  type: 'assembly',
  installProcess: [],
  priceIncludes: [],
};

export { AttachLabor };